import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExperimentEditorFields } from '@/features/im-channel-proactivity-experiments/ExperimentEditorFields';
import type {
  ImChannelProactivityExperiment,
  ImChannelProactivityExperimentKind,
  ProactivenessRollout,
  ProactivenessFilter,
  UpdateImChannelProactivityExperimentInput,
  Weekday,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';
import {
  useCreateImChannelProactivityExperiment,
  useUpdateImChannelProactivityExperiment,
} from '@/features/im-channel-proactivity-experiments/useImChannelProactivityExperiments';

import type { FormEvent } from 'react';

type ExperimentEditorDialogProps = {
  mode: 'create' | 'duplicate' | 'edit';
  experiment?: ImChannelProactivityExperiment;
  rollouts: ProactivenessRollout[];
  section: ProactivenessFilter;
  onOpenChange: (open: boolean) => void;
};

/** Creates, duplicates, or updates one shared experiment definition. */
export const ExperimentEditorDialog = ({
  mode,
  experiment,
  rollouts,
  section,
  onOpenChange,
}: ExperimentEditorDialogProps) => {
  const editing = mode === 'edit';
  const createExperiment = useCreateImChannelProactivityExperiment();
  const updateExperiment = useUpdateImChannelProactivityExperiment();
  const mutation = editing ? updateExperiment : createExperiment;
  const noun =
    (experiment?.section ?? section) === 'experimental'
      ? 'experiment'
      : 'routine';
  const copy = {
    create: {
      title: `Create ${noun}`,
      description: 'Define the shared prompt and daily schedule.',
      submitLabel: `Create ${noun}`,
      pendingLabel: 'Creating...',
    },
    duplicate: {
      title: `Duplicate ${noun}`,
      description: 'Choose a new stable ID for this copy.',
      submitLabel: 'Create copy',
      pendingLabel: 'Creating...',
    },
    edit: {
      title: `Edit ${noun}`,
      description:
        'Changes update every assigned routine. Paused routines stay paused.',
      submitLabel: 'Save changes',
      pendingLabel: 'Saving...',
    },
  }[mode];

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const input: UpdateImChannelProactivityExperimentInput = {
      title: String(form.get('title')),
      description: String(form.get('description')),
      prompt: String(form.get('prompt')),
      kind: String(form.get('kind')) as ImChannelProactivityExperimentKind,
      schedule_times: String(form.get('schedule_times'))
        .split(',')
        .map((time) => time.trim()),
      weekdays: form.getAll('weekdays').map((day) => String(day) as Weekday),
      required_integrations: [
        ...new Set([
          ...form.getAll('required_integrations').map(String),
          ...String(form.get('other_required_integrations'))
            .split(',')
            .map((slug) => slug.trim())
            .filter(Boolean),
        ]),
      ],
    };
    const closeDialog = () => onOpenChange(false);

    if (editing && experiment) {
      updateExperiment.mutate(
        { slug: experiment.slug, section: experiment.section, input },
        { onSuccess: closeDialog }
      );
      return;
    }
    createExperiment.mutate(
      {
        slug: String(form.get('slug')),
        rollout_slug: String(form.get('rollout_slug')),
        ...input,
      },
      { onSuccess: closeDialog }
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl overflow-hidden p-0">
        <form onSubmit={handleSubmit} className="flex min-h-0 w-full flex-col">
          <div className="min-h-0 space-y-6 overflow-y-auto p-6">
            <DialogHeader>
              <DialogTitle>{copy.title}</DialogTitle>
              <DialogDescription>{copy.description}</DialogDescription>
            </DialogHeader>

            <ExperimentEditorFields
              mode={mode}
              experiment={experiment}
              rollouts={rollouts}
            />
          </div>

          {mutation.error && (
            <p
              role="alert"
              className="mx-6 shrink-0 rounded-md border border-destructive px-4 py-3 text-sm text-destructive"
            >
              {mutation.error.message}
            </p>
          )}

          <DialogFooter className="shrink-0 border-t p-6">
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? copy.pendingLabel : copy.submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
